import React, { useState, useEffect, useCallback } from 'react';
import { Button, Card, Checkbox, DatePicker, Dropdown, Menu, message, Modal, Select, Table } from 'antd';
import { Search, ArrowLeft, Send, Eye, MessageSquareReply, AlertCircle, 
         Phone, Mail, MessageCircle, Calendar, CheckCircle2, Download, 
         X, ArrowUpRight,
         MessageSquare,
         Pause,
         Play} from 'lucide-react';
import debounce from 'lodash/debounce';
import  GenericConfirmationModal from '../../components/GenericConfirmationModal';
import { getCampaignById, updateCampaign } from '../../services/broadcast';
import type { CampaignViewProps, CampaignFormData, LeadData, StatusType, ErrorObject, ContentStructure } from './campaignViewTypes';
import { STATUS_COLORS } from './campaignViewTypes';
import type { MenuProps } from 'antd';
import './campaignView.css';
import LeadExportService from '../../services/LeadExportService';
import moment from 'moment';
import ContactLinksGenerator from '../../utils/contactlinksgenerator';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { API_ENDPOINT } from '../../services/userService';
import LeadDetails from '../leads/leadDetails/leadDetails';
import DrawerComponent from '../../components/drawer';
import { useNavigate } from 'react-router-dom';
import { assignLeadInList, copyLeadInList, moveLeadInList } from '../../services/leadService';
import { useAppDispatch } from '../../hooks/hooks';
import { setFilter, clearFilters, fetchConversations } from '../../store/slices/conversationsSlice';
import { getAllLeadList } from '../../services/leadListService';
import { getAutomationList, setManuallyAutomate } from '../../services/automationService';
import GlobalDrawer from '../../components/GlobalDrawer';
import { RxCopy } from 'react-icons/rx';
import { MdOutlineAddTask } from 'react-icons/md';
import SelectLeadStatus from '../../components/status';


const getErrorDisplay = (error: ErrorObject | ErrorObject[] | string | null | undefined): string | null => {
  if (!error) return null;

  // Case 1: If error is a string
  if (typeof error === 'string') {
    try {
      // Try to parse the string as JSON
      const parsed = JSON.parse(error);
      return getErrorDisplay(parsed); // Recursively handle parsed object/array
    } catch {
      // If parsing fails, return the string as-is
      return error;
    }
  }

  // Case 2: If error is an array
  if (Array.isArray(error)) {
    const errorMessages = error
      .map((e) => getErrorDisplay(e)) // Recursively process each element
      .filter(Boolean); // Remove empty/null values
    return errorMessages.length > 0 ? errorMessages.join(', ') : 'Error occurred';
  }

  // Case 3: If error is an object
  if (typeof error === 'object' && error !== null) {
    // Extract error message from possible properties
    return (
      error.error_data?.details ?? // Check error_data.details first
      error.message ?? // Fallback to message
      error.title ?? // Fallback to title
      'Error occurred' // Default fallback
    );
  }

  // Case 4: If error is of an unexpected type
  return 'Error occurred';
};

const renderTemplateOrContent = (campaignData: CampaignFormData) => {
  if (!campaignData) return null;

  const serviceType = campaignData.serviceType?.toLowerCase() ?? '';
  
  // Handle WhatsApp templates
  if (serviceType === 'whatsapp' && campaignData.whatsappTemplate?.name) {
    return (
      <>
        <span>•</span>
        <span>Template: {campaignData.whatsappTemplate.name}</span>
      </>
    );
  }

  // Handle Content
  if (campaignData.content) {
    // If content is a string, render it directly
    if (typeof campaignData.content === 'string') {
      return (
        <>
          <span>•</span>
          <span>Content: {campaignData.content}</span>
        </>
      );
    }

    // If content is our structured object
    if (typeof campaignData.content === 'object' && 'details' in campaignData.content) {
      const { details } = campaignData.content as unknown as ContentStructure;
      const displayText = details.title || details.subject || 'Untitled';
      
      return (
        <>
          <span>•</span>
          <span>Content: {displayText}</span>
        </>
      );
    }
  }

  return null;
};

const LoadingSpinner: React.FC = () => (
  <div className="loading-spinner">Loading...</div>
);

const CampaignView: React.FC<CampaignViewProps> = ({ campaignId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [campaignData, setCampaignData] = useState<CampaignFormData | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [selectedStatus, setSelectedStatus] = useState<StatusType | ''>('');
  const [selectedButtonAction, setSelectedButtonAction] = useState<string>('');
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [formCloseModel, setFormCloseModel] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
const [showRescheduleModal, setShowRescheduleModal] = useState(false);
const [newScheduleDate, setNewScheduleDate] = useState<Date | null>(null);

// Add these state declarations
const [confirmCancelModal, setConfirmCancelModal] = useState<boolean>(false);
const [confirmPauseModal, setConfirmPauseModal] = useState<boolean>(false);

  const [openWithHeader, setOpenWithHeader] = useState(false);
const [selectedLead, setSelectedLead] = useState<any>(null);

const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
const [allLeadsSelected, setAllLeadsSelected] = useState(false);
const navigate = useNavigate();
const dispatch = useAppDispatch();

// Bulk operations state
const [assignLeadDrawer, setAssignLeadDrawer] = useState(false);
const [copyLeadDrawer, setCopyLeadDrawer] = useState(false);
const [automationDrawer, setAutomationDrawer] = useState(false);
const [activeLeadOption, setActiveLeadOption] = useState("copy");
const [leadListData, setLeadListData] = useState<any>([]);
const [automationList, setAutomationList] = useState<any>([]);

// Status editing state
const [showStatusPopup, setShowStatusPopup] = useState<boolean>(false);
const [bulkSelectedStatus, setBulkSelectedStatus] = useState<string>("");

// Template quick reply buttons state
const [templateButtons, setTemplateButtons] = useState<any[]>([]);


const onLeadClick = (lead: any) => {
  if (lead?._id) {
    setSelectedLead(lead);
    setOpenWithHeader(true);
  } else {
    message.error('Invalid lead selected');
  }
};

  const fetchCampaignData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getCampaignById(campaignId, {
        page: currentPage,
        perPage: pageSize,
        status: selectedStatus || undefined,
        search: searchTerm || undefined,
        buttonAction: selectedButtonAction || undefined,
        orderBy: 'lastActivity',
        isAscending: false
      });

      setCampaignData(data.data);
    } catch (error) {
      console.error('Error fetching campaign details:', error);
    } finally {
      setLoading(false);
    }
  }, [campaignId, currentPage, pageSize, selectedStatus, searchTerm, selectedButtonAction]);

  // Function to extract template buttons for WhatsApp campaigns
  const extractTemplateButtons = useCallback(() => {
    if (!campaignData?.whatsappTemplate?.components || campaignData?.serviceType?.toLowerCase() !== 'whatsapp') {
      setTemplateButtons([]);
      return;
    }

    const buttonComponents = campaignData.whatsappTemplate.components.filter((component: any) =>
      component.type === 'BUTTONS' && component.buttons
    );

    const allButtons: any[] = [];
    buttonComponents.forEach((component: any) => {
      component.buttons.forEach((button: any) => {
        if (button.type === 'QUICK_REPLY') {
          allButtons.push({
            text: button.text,
            value: button.text // Use text as value for filtering
          });
        }
      });
    });

    setTemplateButtons(allButtons);
  }, [campaignData?.whatsappTemplate, campaignData?.serviceType]);

  useEffect(() => {
    fetchCampaignData();
    handleGetLeadList();
    handleGetAutomationList();
  }, [fetchCampaignData]);

  useEffect(() => {
    extractTemplateButtons();
  }, [extractTemplateButtons]);


  // Debounced search handler
  const handleSearch = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
      setCurrentPage(1);
    }, 300),
    []
  );

// Update the handleSelectAllLeads function with proper null checking
const handleSelectAllLeads = async (checked: boolean) => {
  if (checked) {
    // If we're selecting all, we need to get all campaign leads, not just the current page
    setAllLeadsSelected(true);
    
    try {
      // Make sure campaignData is not null before accessing properties
      if (!campaignData) {
        message.error('Campaign data not available');
        return;
      }
      
      // We need to get all leads for this campaign, not just the current page
      const { data } = await getCampaignById(campaignId, {
        page: 1,
        perPage: campaignData.totalLeads || 1000, // Fallback to a large number if totalLeads is undefined
        status: selectedStatus || undefined,
        search: searchTerm || undefined,
        orderBy: 'lastActivity',
        isAscending: false
      });
      
      if (data?.data?.leads) {
        const allLeadIds = data.data.leads.map((lead: any) => lead._id);
        setSelectedLeads(allLeadIds);
        message.success(`Selected all ${allLeadIds.length} leads`);
      }
    } catch (error) {
      console.error('Error fetching all leads:', error);
      message.error('Failed to select all leads');
      setAllLeadsSelected(false);
    }
  } else {
    // If unchecking, clear all selections
    setSelectedLeads([]);
    setAllLeadsSelected(false);
  }
};
  
const CampaignActions: React.FC<{
  status: string;
  isPaused?: boolean;
  onPause: () => void;
  onCancel: () => void;
  onReschedule: () => void;
}> = ({ status, isPaused, onPause, onCancel, onReschedule }) => {
  // Determine which actions are available based on status
  const canPause = ['pending', 'approved', 'started', 'in_progress', 'paused', 'started_sending', 'scheduled'].includes(status.toLowerCase());
  const canCancel = ['pending', 'approved', 'started', 'in_progress', 'paused', 'started_sending', 'scheduled'].includes(status.toLowerCase());
  const canReschedule = ['pending', 'approved', 'paused'].includes(status.toLowerCase());

  return (
    <div className="campaign-actions">
      {canPause && (
        <Button 
          onClick={onPause}
          className={isPaused ? "resume-btn" : "pause-btn"}
        >
          {isPaused ? (
            <>
              <Play size={16} />&nbsp;Resume
            </>
          ) : (
            <>
              <Pause size={16} />&nbsp;Pause
            </>
          )}
        </Button>
      )}
      
      {canCancel && (
        <Button onClick={onCancel} className="cancel-btn">
          <X size={16} />&nbsp;Cancel
        </Button>
      )}
      
      {canReschedule && (
        <Button onClick={onReschedule} className="reschedule-btn">
          <Calendar size={16} />&nbsp;Reschedule
        </Button>
      )}
    </div>
  );
};

  // Add this function to create a new campaign with selected leads
  const handleCreateCampaign = () => {
    if (selectedLeads.length > 0) {
      // Store selected lead IDs in localStorage
      localStorage.setItem('selectedCampaignLeads', JSON.stringify(selectedLeads));
      message.success(`Creating campaign with ${selectedLeads.length} leads`);
      navigate('/createWhatsappBroadcast');
    }
  };

  // Function to open WhatsApp inbox with lead filters
  const handleOpenInbox = () => {
    if (selectedLeads.length > 0) {
      // Clear existing filters and set leadIds filter
      dispatch(clearFilters());
      dispatch(setFilter({ key: 'leadIds', value: selectedLeads }));

      // Navigate to WhatsApp inbox
      message.success(`Opening WhatsApp inbox with ${selectedLeads.length} filtered leads`);
      navigate('/whatsappInbox');

      // Fetch conversations with the new filter
      dispatch(fetchConversations({ page: 1, refresh: true }));
    } else {
      message.warning('Please select leads to filter conversations');
    }
  };

  // Bulk operations functions
  const handleGetLeadList = async () => {
    try {
      const response = await getAllLeadList({
        isAscending: false,
        page: 1,
        perPage: 100,
      });
      if (response && response.status) {
        let defaultLead = {
          name: "Default lead list",
          _id: "",
        };
        setLeadListData([defaultLead, ...response.data.data]);
      }
    } catch (err) {
      console.error('Error fetching lead lists:', err);
    }
  };

  const handleGetAutomationList = async () => {
    try {
      const response = await getAutomationList();
      if (response && response.status) {
        setAutomationList(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching automation list:', err);
    }
  };

  // Bulk operation handlers
  const handleAssignLead = async (assignToUser: string) => {
    try {
      if (selectedLeads.length === 0) {
        message.error('Please select leads to assign');
        return;
      }

      const { data } = await assignLeadInList({ leadIds: selectedLeads, assignToUser });
      message.success(data?.message || 'Leads assigned successfully');
      setAssignLeadDrawer(false);
      setSelectedLeads([]);
      setAllLeadsSelected(false);
      fetchCampaignData(); // Refresh data
    } catch (error) {
      console.error('Error assigning leads:', error);
      message.error('Failed to assign leads');
    }
  };

  const handleCopyLead = async (targetListId: string, activeLeadOption: string) => {
    try {
      if (selectedLeads.length === 0) {
        message.error('Please select leads to copy/move');
        return;
      }

      const data = {
        leadIDs: selectedLeads,
        targetListId: targetListId === "" ? null : targetListId,
      };

      if (activeLeadOption === "copy") {
        const response = await copyLeadInList(data);
        message.success(response?.data?.message || 'Leads copied successfully');
      } else {
        const response = await moveLeadInList(data);
        message.success(response?.data?.message || 'Leads moved successfully');
      }

      setCopyLeadDrawer(false);
      setSelectedLeads([]);
      setAllLeadsSelected(false);
      fetchCampaignData(); // Refresh data
    } catch (error) {
      console.error('Error copying/moving leads:', error);
      message.error(`Failed to ${activeLeadOption} leads`);
    }
  };

  const handleSelectAutomate = async (automationId: string) => {
    try {
      if (selectedLeads.length === 0) {
        message.error('Please select leads to add to automation');
        return;
      }

      const data = {
        leadIds: selectedLeads,
        automationId,
      };

      const response = await setManuallyAutomate(data);
      message.success(response?.data?.message || 'Leads added to automation successfully');
      setAutomationDrawer(false);
      setSelectedLeads([]);
      setAllLeadsSelected(false);
    } catch (error) {
      console.error('Error adding leads to automation:', error);
      message.error('Failed to add leads to automation');
    }
  };

  // Status and Label handlers
  const toggleLeadStatus = () => {
    setShowStatusPopup(!showStatusPopup);
    setSelectedStatus("");
  };

  const onStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBulkSelectedStatus(e.target.value);
  };

  const onEditStatus = () => {
    if (selectedLeads.length === 0) {
      message.error('Please select leads to edit status');
      return;
    }
    setShowStatusPopup(true);
  };



  // Add this with other handlers in CampaignView component
const handleStatusChange = (value: StatusType | '') => {
  setSelectedStatus(value);
  setCurrentPage(1); // Reset to first page when status changes
};

// Add immediate input handler
const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const value = e.target.value;
  setInputValue(value);  // Update UI immediately
  handleSearch(value);   // Debounced API call
};

const handleExportModalToggle = () => {
  setIsExportModalOpen(!isExportModalOpen);
};

// Modify clear handler to clear both states
const handleClearSearch = () => {
  setInputValue('');
  setSearchTerm('');
  setCurrentPage(1);
};


const { userDetails } = useSelector((state: any) => {
  return state?.rootReducers.userData;
});

const StateData = useSelector((state: any) => {
  return state?.rootReducers;
});

const teamMembers =
  StateData?.userData?.userDetails?.organizationEmployee
  ?.filter((item: any) =>
    item?._id !== StateData?.userData?.userDetails?._id && // Exclude current user
    item?.isActive === true // Only active users
  )
  .map((item: any) => ({
    value: item?._id,
    label: `${item?.firstName} ${item?.lastName}`, // Space between names
    role: item?.role // Include role for additional display
  }));

// Handler functions
const handlePauseToggle = async () => {
  try {
    setActionLoading('pause');
    await updateCampaign(campaignId, { 
      isPaused: !campaignData?.isPaused 
    });
    
    // Refresh campaign data
    fetchCampaignData();
    message.success(campaignData?.isPaused ? 'Campaign resumed' : 'Campaign paused');
    setConfirmPauseModal(false); // Add this line
  } catch (error) {
    console.error('Error toggling pause state:', error);
    message.error('Failed to update campaign status');
  } finally {
    setActionLoading(null);
  }
};

const handleCancel = async () => {
  try {
    setActionLoading('cancel');
    await updateCampaign(campaignId, { isCancelled: true });
    
    // Refresh campaign data
    fetchCampaignData();
    message.success('Campaign cancelled');
    setConfirmCancelModal(false); // Add this line
  } catch (error) {
    console.error('Error cancelling campaign:', error);
    message.error('Failed to cancel campaign');
  } finally {
    setActionLoading(null);
  }
};

const handleReschedule = async () => {
  if (!newScheduleDate) {
    message.error('Please select a new date');
    return;
  }
  
  try {
    setActionLoading('reschedule');
    await updateCampaign(campaignId, { 
      scheduleAt: moment(newScheduleDate).utc().format() 
    });
    
    // Refresh campaign data
    fetchCampaignData();
    message.success('Campaign rescheduled');
    setShowRescheduleModal(false);
  } catch (error) {
    console.error('Error rescheduling campaign:', error);
    message.error('Failed to reschedule campaign');
  } finally {
    setActionLoading(null);
  }
};



const confirmExport = async () => {
  try {
    await LeadExportService.exportLeads(
      campaignId,
      {
        status: selectedStatus,
        searchTerm: searchTerm,
        buttonAction: selectedButtonAction
      }
    );

    setIsExportModalOpen(false);
  } catch (error) {
    console.error('Export error:', error);
    setIsExportModalOpen(false);
  }
};

// Helper function to determine if the service is an email type
const isEmailService = (serviceType: string | undefined): boolean => {
  if (!serviceType) return false;
  const type = serviceType.toLowerCase();
  return type === 'zeptomail' || type === 'gmail' || type === 'brevo' || type === 'email';
};


  // Keeping the styling components
  const StatusBadge: React.FC<{ status: StatusType }> = ({ status }) => (
    <span className="status-badge" style={{ backgroundColor: STATUS_COLORS[status] }}>
      {status}
    </span>
  );

  const ActionButtons: React.FC<{ lead: LeadData }> = ({ lead }) => {
    const handleAction = (url: string) => {
      window.open(url, '_blank');
    };
  
    const contactLinks = new ContactLinksGenerator({
      number: lead.phone || '',
      email: lead.email || '',
      message: `Hi ${lead.name || ''},`
    });
  
    return (
      <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-2">
        {lead.phone && (
          <button 
            onClick={() => handleAction(contactLinks.tel)}
            className="p-2.5 hover:bg-gray-100 rounded-full transition-colors duration-200"
            title="Call"
          >
            <Phone size={16} strokeWidth={2} className="text-[#1677FF]" />
          </button>
        )}
        
        {lead.email && (
          <button 
            onClick={() => handleAction(contactLinks.mail)}
            className="p-2.5 hover:bg-gray-100 rounded-full transition-colors duration-200"
            title="Email"
          >
            <Mail size={16} strokeWidth={2} className="text-[#F1B003]" />
          </button>
        )}
        
        {lead.phone && (
          <button 
            onClick={() => handleAction(contactLinks.whatsapp)}
            className="p-2.5 hover:bg-gray-100 rounded-full transition-colors duration-200"
            title="WhatsApp"
          >
            <MessageCircle size={16} strokeWidth={2} className="text-[#4CB050]" />
          </button>
        )}

        <button 
          onClick={(e) => {
            e.stopPropagation();
            onLeadClick(lead);
          }}
          className="p-2.5 hover:bg-gray-100 rounded-full transition-colors duration-200"
          title="View Lead Details"
        >
          <ArrowUpRight size={16} strokeWidth={2} className="text-gray-600" />
        </button>
      </div>
    );
};

  if (!campaignData) {
    return <LoadingSpinner />;
  }
  


 

 return (
  <div className="campaign-view">
<div className="campaign-header">
  <div className="header-content">
    <button className="back-button" onClick={onClose}>
      <ArrowLeft size={24} />
    </button>
    <div className="campaign-info">
      <div className="campaign-title-row">
        <h1>{campaignData.name}</h1>
        {/* Add the campaign status badge here */}
        <span 
          className={`status-badge ${campaignData.status.toLowerCase()}`}
          style={{ 
            backgroundColor: STATUS_COLORS[campaignData.status.toLowerCase() as StatusType] || '#8c8c8c',
            marginLeft: '12px',
            fontSize: '13px',
            padding: '4px 12px'
          }}
        >
          {campaignData.status.charAt(0).toUpperCase() + campaignData.status.slice(1)}
        </span>
      </div>
      <div className="campaign-meta">
        <span>
          Sent by {campaignData.createdBy.firstName} {campaignData.createdBy.lastName}
        </span>
        <span>•</span>
        <span><Calendar size={14} className="inline mr-1" /> Scheduled for: {moment.utc(campaignData.scheduleAt).local().format('Do MMM YYYY [at] h:mm A')}</span>
        {renderTemplateOrContent(campaignData)}
      </div>
      {campaignData.error && getErrorDisplay(campaignData.error) && (
        <div className="error-message text-red-500 mt-1 text-sm">
          {getErrorDisplay(campaignData.error)}
        </div>
      )}
    </div>
    {/* The info message and action buttons remain unchanged */}
    <div className="info-message">
  <AlertCircle size={16} strokeWidth={2} className="info-icon" />
  <span>Metrics update in real-time</span>
</div>
    <div className="header-actions">
      <CampaignActions
        status={campaignData.status}
        isPaused={campaignData.isPaused}
        onPause={handlePauseToggle}
        onCancel={() => setConfirmCancelModal(true)}
        onReschedule={() => setShowRescheduleModal(true)}
      />
    </div>
  </div>
</div>
   




{/* Cancel Confirmation Modal */}
<GenericConfirmationModal
  showModal={confirmCancelModal}
  toggleModal={() => setConfirmCancelModal(false)}
  onConfirmation={handleCancel}
  title="Cancel Campaign"
  message="Are you sure you want to cancel this campaign? This action cannot be undone."
  confirmText="Cancel Campaign"
  cancelText="Keep Campaign Active"
  isWarning={true}
/>

<Modal
  title="Reschedule Campaign"
  visible={showRescheduleModal}
  onCancel={() => setShowRescheduleModal(false)}
  onOk={handleReschedule}
  confirmLoading={actionLoading === 'reschedule'}
>
  <div className="reschedule-form">
    <div className="form-group">
      <label>New Schedule Date and Time:</label>
      <DatePicker
        showTime
        format="YYYY-MM-DD HH:mm"
        onChange={(date) => setNewScheduleDate(date as any)}
        disabledDate={(date) => date.isBefore(moment(), 'day')}
        className="full-width"
      />
    </div>
  </div>
</Modal>
{/* Pause/Resume Confirmation Modal */}
<GenericConfirmationModal
  showModal={confirmPauseModal}
  toggleModal={() => setConfirmPauseModal(false)}
  onConfirmation={handlePauseToggle}
  title={campaignData?.isPaused ? "Resume Campaign" : "Pause Campaign"}
  message={campaignData?.isPaused 
    ? "Are you sure you want to resume this campaign? Messages will continue to be sent." 
    : "Are you sure you want to pause this campaign? No messages will be sent until resumed."}
  confirmText={campaignData?.isPaused ? "Resume" : "Pause"}
  cancelText="Cancel"
  isWarning={false}
/>

    <div className="campaign-content">
     <div className="metrics-grid">
        {isEmailService(campaignData.serviceType) ? (
          // Email Campaign Metrics
          <>
            <Card className="metric-card">
              <div className="metric-header">
                <div className="metric-title">
                  <span>Queued</span>
                  <Send size={18} color="#1890ff" />
                </div>
                <span className="metric-value">{campaignData.totalQueued || 0}</span>
              </div>
              <div className="metric-description">Total emails queued</div>
            </Card>

            <Card className="metric-card">
              <div className="metric-header">
                <div className="metric-title">
                  <span>Sent</span>
                  <CheckCircle2 size={18} color="#52c41a" />
                </div>
                <span className="metric-value">{campaignData.totalMailSent || 0}</span>
              </div>
              <div className="metric-description">Emails sent successfully</div>
            </Card>

            <Card className="metric-card">
              <div className="metric-header">
                <div className="metric-title">
                  <span>Opened</span>
                  <Eye size={18} color="#722ed1" />
                </div>
                <span className="metric-value">{campaignData.totalOpened || 0}</span>
              </div>
              <div className="metric-description">Emails opened</div>
            </Card>

            <Card className="metric-card">
              <div className="metric-header">
                <div className="metric-title">
                  <span>Clicked</span>
                  <ArrowUpRight size={18} color="#eb2f96" />
                </div>
                <span className="metric-value">{campaignData.totalClicked || 0}</span>
              </div>
              <div className="metric-description">Link clicks</div>
            </Card>
          </>
        ) : (
          // WhatsApp Campaign Metrics
          <>
            <Card className="metric-card">
              <div className="metric-header">
                <div className="metric-title">
                  <span>Sent</span>
                  <Send size={18} color="#1890ff" />
                </div>
                <span className="metric-value">{campaignData.totalSent}</span>
              </div>
              <div className="metric-description">Total messages sent</div>
            </Card>

            <Card className="metric-card">
              <div className="metric-header">
                <div className="metric-title">
                  <span>Delivered</span>
                  <CheckCircle2 size={18} color="#52c41a" />
                </div>
                <span className="metric-value">{campaignData.totalDelivered}</span>
              </div>
              <div className="metric-description">Successfully delivered</div>
            </Card>

            <Card className="metric-card">
              <div className="metric-header">
                <div className="metric-title">
                  <span>Read</span>
                  <Eye size={18} color="#722ed1" />
                </div>
                <span className="metric-value">{campaignData.totalRead}</span>
              </div>
              <div className="metric-description">Messages read</div>
            </Card>

            <Card className="metric-card">
              <div className="metric-header">
                <div className="metric-title">
                  <span>Replied</span>
                  <MessageSquareReply size={18} color="#eb2f96" />
                </div>
                <span className="metric-value">{campaignData.totalReplied}</span>
              </div>
              <div className="metric-description">Responses received</div>
            </Card>
          </>
        )}
      </div>

      <div className="metrics-grid-secondary">
  {isEmailService(campaignData.serviceType) ? (
    // Email Campaign Secondary Metrics
    <>
      <Card className="metric-card">
        <div className="metric-header">
          <div className="metric-title">
            <span>Processed</span>
            <AlertCircle size={18} color="#faad14" />
          </div>
          <span className="metric-value">{campaignData.totalProcessed || 0}</span>
        </div>
        <div className="metric-description">Emails processed</div>
      </Card>

      <Card className="metric-card">
        <div className="metric-header">
          <div className="metric-title">Open Rate</div>
          <span className="metric-value">{campaignData.openRate || 0}%</span>
        </div>
        <div className="metric-description">Email open percentage</div>
      </Card>

      <Card className="metric-card">
        <div className="metric-header">
          <div className="metric-title">Click Rate</div>
          <span className="metric-value">{campaignData.clickRate || 0}%</span>
        </div>
        <div className="metric-description">Link click percentage</div>
      </Card>

      <Card className="metric-card">
        <div className="metric-header">
          <div className="metric-title">
            <span>Daily Limit</span>
            <Calendar size={18} color="#1890ff" />
          </div>
          <span className="metric-value">{campaignData.dailySentCount || 0}/{campaignData.dailyLimit || 0}</span>
        </div>
        <div className="metric-description">Daily sending progress</div>
      </Card>
    </>
  ) : (
    // WhatsApp Campaign Secondary Metrics
    <>
      <Card className="metric-card">
        <div className="metric-header">
          <div className="metric-title">
            <span>Failed</span>
            <AlertCircle size={18} color="#f5222d" />
          </div>
          <span className="metric-value">{campaignData.totalFailed}</span>
        </div>
        <div className="metric-description">Failed deliveries</div>
      </Card>

      <Card className="metric-card">
        <div className="metric-header">
          <div className="metric-title">Delivery Rate</div>
          <span className="metric-value">{campaignData.totalDeliveredRate}%</span>
        </div>
        <div className="metric-description">Successful delivery percentage</div>
      </Card>

      <Card className="metric-card">
        <div className="metric-header">
          <div className="metric-title">Read Rate</div>
          <span className="metric-value">{campaignData.totalReadRate}%</span>
        </div>
        <div className="metric-description">Message read percentage</div>
      </Card>

      <Card className="metric-card">
        <div className="metric-header">
          <div className="metric-title">Reply Rate</div>
          <span className="metric-value">{campaignData.totalRepliedRate}%</span>
        </div>
        <div className="metric-description">Message reply percentage</div>
      </Card>
    </>
  )}
</div>

      <Card>
        <div className="leads-table-header">
        <h2>Campaign Leads ({campaignData.totalLeads})</h2>
          <div className="leads-controls">
            <Select
              className="status-select"
              placeholder="Filter Status"
              style={{ width: 160 }}
              value={selectedStatus}
              onChange={handleStatusChange}
              options={[
                { value: '', label: 'All Status' },
                { value: 'sent', label: 'Sent' },
                { value: 'delivered', label: 'Delivered' },
                { value: 'read', label: 'Read' },
                { value: 'replied', label: 'Replied' },
                { value: 'failed', label: 'Failed' },
                ...(isEmailService(campaignData.serviceType) ? [
                  { value: 'opened', label: 'Opened' },
                  { value: 'clicked', label: 'Clicked' }
                ] : [])
              ]}
            />

            {/* Button Action Filter - Only show for WhatsApp campaigns with template buttons */}
            {campaignData?.serviceType?.toLowerCase() === 'whatsapp' && templateButtons.length > 0 && (
              <Select
                className="button-action-select"
                placeholder="Filter by Button"
                style={{ width: 180 }}
                value={selectedButtonAction}
                onChange={(value) => {
                  setSelectedButtonAction(value);
                  setCurrentPage(1);
                }}
                loading={false}
                options={[
                  { value: '', label: 'All Buttons' },
                  ...templateButtons.map(button => ({
                    value: button.value,
                    label: button.text
                  }))
                ]}
              />
            )}
            
            <div className="search-box">
  <Search size={18} strokeWidth={2} className="search-icon" />
  <input
    type="text"
    placeholder="Search leads..."
    value={inputValue}
    onChange={handleInputChange}
  />
  {inputValue && (
    <X 
      size={18}
      strokeWidth={2}
      className="cursor-pointer clear-search-icon"
      onClick={handleClearSearch}
    />
  )}
</div>
<button 
      className="export-button"
      onClick={handleExportModalToggle}
    >
      <Download size={18} strokeWidth={2} />
      <span>Export</span>
    </button>
    <GenericConfirmationModal 
  showModal={isExportModalOpen} 
  toggleModal={handleExportModalToggle} 
  onConfirmation={confirmExport}
  title="Confirm Export" 
  message={`Export ${campaignData?.totalLeads || 0} leads?
    ${selectedStatus ? `Filtered by status: ${selectedStatus}` : ''}
    ${selectedButtonAction ? `Filtered by button: ${selectedButtonAction}` : ''}
    ${searchTerm ? `Searching for: "${searchTerm}"` : ''}`}
  confirmText="Export"
  cancelText="Cancel"
  isWarning={false}
/>
  
          </div>
        </div>

        {selectedLeads.length > 0 && (
  <div className="mb-3">
    <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
      <div>
        <span className="text-muted fw-bold">{selectedLeads.length} Lead{selectedLeads.length > 1 ? 's' : ''} Selected</span>
      </div>

      <div className="d-flex flex-wrap gap-2">
        <button
          onClick={() => setAssignLeadDrawer(true)}
          style={{
            background: 'white',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            padding: '6px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#40a9ff';
            e.currentTarget.style.color = '#40a9ff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#d9d9d9';
            e.currentTarget.style.color = 'inherit';
          }}
        >
          <img src="/assets/img/assign.png" style={{ width: '16px', height: '16px' }} alt="" />
          Bulk assign
        </button>

        <button
          onClick={() => {
            setCopyLeadDrawer(true);
            setActiveLeadOption("copy");
          }}
          style={{
            background: 'white',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            padding: '6px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#40a9ff';
            e.currentTarget.style.color = '#40a9ff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#d9d9d9';
            e.currentTarget.style.color = 'inherit';
          }}
        >
          <RxCopy size={16} />
          Copy to list
        </button>

        <button
          onClick={() => {
            setCopyLeadDrawer(true);
            setActiveLeadOption("move");
          }}
          style={{
            background: 'white',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            padding: '6px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#40a9ff';
            e.currentTarget.style.color = '#40a9ff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#d9d9d9';
            e.currentTarget.style.color = 'inherit';
          }}
        >
          <img src="/assets/img/move.png" style={{ width: '16px', height: '16px' }} alt="" />
          Move to list
        </button>

        <button
          onClick={onEditStatus}
          style={{
            background: 'white',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            padding: '6px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#40a9ff';
            e.currentTarget.style.color = '#40a9ff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#d9d9d9';
            e.currentTarget.style.color = 'inherit';
          }}
        >
          <img src="/assets/img/edit_status.png" style={{ width: '16px', height: '16px' }} alt="" />
          Edit status
        </button>


        <button
          onClick={() => setAutomationDrawer(true)}
          style={{
            background: 'white',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            padding: '6px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#40a9ff';
            e.currentTarget.style.color = '#40a9ff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#d9d9d9';
            e.currentTarget.style.color = 'inherit';
          }}
        >
          Add to automation
        </button>

        <button
          className="create-campaign-btn-modern"
          onClick={handleCreateCampaign}
        >
          <MessageSquare size={18} strokeWidth={2} />
          <span>Create Campaign</span>
        </button>

        <button
          onClick={handleOpenInbox}
          style={{
            background: '#1677ff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#4096ff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#1677ff';
          }}
        >
          <MessageCircle size={18} strokeWidth={2} />
          <span>Open in Inbox</span>
        </button>
      </div>
    </div>
  </div>
)}

        <Table
          loading={loading}
          dataSource={campaignData.leads}
          columns={[
            {
              title: () => (
                <Checkbox 
                  checked={allLeadsSelected || (campaignData?.leads && selectedLeads.length === campaignData.leads.length && selectedLeads.length > 0)}
                  onChange={(e) => handleSelectAllLeads(e.target.checked)}
                />
              ),
              key: 'selection',
              width: '50px',
              render: (_, record) => (
                <Checkbox
                  checked={selectedLeads.includes(record._id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedLeads(prev => [...prev, record._id]);
                    } else {
                      setSelectedLeads(prev => prev.filter(id => id !== record._id));
                      setAllLeadsSelected(false);
                    }
                  }}
                />
              )
            },
            {
              title: 'Lead Name',
              dataIndex: 'name',
              key: 'name',
              render: (name: string) => name || 'N/A'
            },
            {
              title: 'Status',
              dataIndex: 'messageStatus',
              key: 'messageStatus',
              render: (status: StatusType) => {
                const colorMap: { [key: string]: string } = {
                  sent: '#1890ff',      // Blue
                  delivered: '#52c41a',  // Green
                  read: '#722ed1',      // Purple
                  replied: '#eb2f96',   // Pink
                  failed: '#f5222d' 
                };
            
                const color = colorMap[status?.toLowerCase()] || '#8c8c8c';
                
                return (
                  <span className="status-badge" style={{ backgroundColor: color }}>
                    {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'N/A'}
                  </span>
                );
              }
            },
            {
              title: 'Last Activity',
              key: 'lastActivity',
              render: (_, record) => record.lastActivityLabel || 'No activity',
            },
            {
              title: 'Actions',
              key: 'actions',
              render: (_, record) => <ActionButtons lead={record} />,
            },
          ]}
          pagination={{
            total: campaignData.totalLeads,
            pageSize: pageSize,
            current: currentPage,
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: false,
            showTotal: (total) => `Total ${total} items`,
            position: ['bottomCenter']
          }}
          scroll={{ y: 400 }}
          className="leads-table"
        />
      </Card>
      {selectedLead && (
  <DrawerComponent
    openWithHeader={openWithHeader}
    setOpenWithHeader={setOpenWithHeader}
    customHeader={false}
    drawerTitle="Lead Details"
    size="lg"
    className="lead_details"
  >
   <LeadDetails
  _id={selectedLead._id}
  name={selectedLead.name || ''}
  integration={selectedLead.integration || ''}
  customSource={selectedLead.customSource || ''}
  createdAt={selectedLead.createdAt || new Date()}
  status={selectedLead.status || []}
  saleValue={selectedLead.saleValue || ''}
  website={selectedLead.website || ''}
  label={selectedLead.label || []}
  // Required callback props with no-op functions
  deleteLeadFromDrawer={() => {}}
  copyLeadToList={() => {}}
  moveLeadToList={() => {}}
  onLabelClick={() => {}}
  onStatusClick={() => {}}
  setFrom={() => {}}
  handleEditModal={() => {}}
  handleLeadDetailsModalOpen={() => {}}
  onLeadFileUpload={() => {}}
  editLead={() => {}}
  leadFileDelete={() => {}}
/>
  </DrawerComponent>
)}

{/* Bulk Operations Drawers */}
{/* Assign Lead Drawer */}
<GlobalDrawer
  openWithHeader={assignLeadDrawer}
  setOpenWithHeader={() => setAssignLeadDrawer(false)}
  drawerTitle="Assign Leads"
  size="xs"
  customHeader
>
  <label className="form-label mb-3">Select team member</label>
  <div>
    <button
      onClick={() => {
        handleAssignLead(StateData?.userData?.userDetails?._id);
      }}
      className="d-block bg-white w-100 p-3 rounded mb-2 border"
      style={{ margin: "8px 0px", textAlign: "left" }}
    >
      <strong>Assign to self</strong>
    </button>
    {teamMembers?.map((item: any, idx: number) => {
      return (
        <button
          key={idx}
          onClick={() => handleAssignLead(item.value)}
          className="d-block bg-white w-100 p-3 rounded mb-2 border"
          style={{ margin: "8px 0px", textAlign: "left" }}
        >
          {item.label.replace("null", "")}
        </button>
      );
    })}
  </div>
</GlobalDrawer>

{/* Copy/Move Lead Drawer */}
<GlobalDrawer
  openWithHeader={copyLeadDrawer}
  setOpenWithHeader={() => setCopyLeadDrawer(false)}
  drawerTitle={activeLeadOption === "copy" ? "Copy Leads" : "Move Leads"}
  size="xs"
  customHeader
>
  <label className="form-label mb-3">Select destination lead list</label>
  <div>
    {leadListData?.length > 0
      ? leadListData?.map((item: any, ix: number) => {
          return (
            <button
              onClick={() => handleCopyLead(item._id, activeLeadOption)}
              key={ix}
              className="d-block bg-white w-100 p-3 rounded mb-2 border"
              style={{ margin: "8px 0px", textAlign: "left" }}
            >
              {item.name}
            </button>
          );
        })
      : "No lead lists found"}
  </div>
</GlobalDrawer>

{/* Automation Drawer */}
<GlobalDrawer
  openWithHeader={automationDrawer}
  setOpenWithHeader={() => setAutomationDrawer(false)}
  drawerTitle="Add to Automation"
  size="xs"
  customHeader
>
  <label className="form-label mb-3">Select automation workflow</label>
  <div>
    {automationList?.length > 0
      ? automationList?.map((item: any, ix: number) => {
          return (
            <button
              onClick={() => handleSelectAutomate(item._id)}
              key={ix}
              className="d-block bg-white w-100 p-3 rounded mb-2 border"
              style={{ margin: "8px 0px", textAlign: "left" }}
            >
              <div>
                <div className="fw-bold">{item.name}</div>
                {item.description && (
                  <div className="text-muted small">{item.description}</div>
                )}
              </div>
            </button>
          );
        })
      : "No automation workflows found"}
  </div>
</GlobalDrawer>

{/* Status Components */}

<SelectLeadStatus
  showStatusPopup={showStatusPopup}
  toggleLeadStatus={toggleLeadStatus}
  onStatusChange={onStatusChange}
  selectedStatus={bulkSelectedStatus}
  setShowStatusPopup={setShowStatusPopup}
  from="campaign"
  id={selectedLeads.join(',')}
  leadList={campaignId}
  getLeads={() => fetchCampaignData()}
  setOpenWithHeader={() => {}}
  onLeadUpdate={() => {
    setSelectedLeads([]);
    setAllLeadsSelected(false);
    fetchCampaignData();
  }}
/>

      </div>
  </div>
);
};
export default CampaignView;